/**
 * OnboardingHighlights — visual emphasis layer for the empty-state demo.
 *
 * Matt 2026-05-26 (#engineering-jeff):
 *
 *   "Highlight in the UI where each step is happening. Give me 3 design
 *    ideas for that eg a yellow highlighter scribble over each step ie
 *    the top of the swimlanes when step 3 is happening etc"
 *
 * After reviewing mockups Matt picked the hybrid B+C approach:
 *
 *   - Steps 2 (prompt input) + 3 (compiled-query swim lanes): YELLOW
 *     HIGHLIGHTER SCRIBBLE behind the text. Says "look at the words".
 *   - Steps 1 (video source button) + 4 (submit button) + 5 (result pill):
 *     SOFT CYAN GLOW ring around the element. Says "look at this
 *     interactive thing".
 *
 * Why split: text gets a marker over it (organic, hand-drawn feel that
 * doesn't compete with the UI chrome); UI elements get a halo that draws
 * the eye without obscuring the affordance.
 *
 * ─── Implementation ──────────────────────────────────────────────────
 *
 * Overlay is `position: fixed`, full-viewport, `pointer-events: none`. It
 * mounts ONE highlight at a time, derived from `phase`. The target is
 * resolved by data-testid lookup against the document; missing targets
 * silently skip (e.g. when running in a unit test without the InputBar).
 *
 * The bounding box is measured via getBoundingClientRect() on every
 * resize / scroll / phase change. Animations are short (200–280ms fade)
 * so the highlight transitions naturally between steps without leaving
 * stale rectangles on screen.
 *
 * Performance: no requestAnimationFrame loop. We measure on phase change
 * and on resize/scroll only (passive listeners). Re-measure is cheap
 * (single getBoundingClientRect per phase).
 *
 * Mobile: the overlay matches mobile target rects identically — the
 * coach mark below the InputBar is fine because the highlight sits ABOVE
 * the target, not in the coach area.
 *
 * Accessibility: aria-hidden everywhere. Pure decoration; the coach mark
 * is the canonical step indicator for AT users.
 */
import { useEffect, useRef, useState, type ReactElement, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

// Reuse the OnboardingCoach Phase contract so callers can pass the same
// `phase` they already feed into the coach. Local typedef avoids a
// circular import with OnboardingCoach.tsx.
export type OnboardingHighlightPhase =
  | 'pre-start'
  | 'typing'
  | 'parsing'
  | 'lanes'
  | 'streaming'
  | 'celebrate'
  | 'settled'
  | 'dismissed'

/** Highlight kind drives the visual treatment. */
type HighlightKind = 'scribble' | 'glow' | null

interface HighlightTarget {
  /** Document-wide data-testid the highlight should track. */
  testid: string
  kind: HighlightKind
  /** Optional padding (in px) added around the target rect on each side. */
  pad?: number
}

/**
 * Phase → target mapping. The 5 onboarding steps map onto the 8-phase
 * state machine like this:
 *
 *   Coach step 1 (Select video source) → 'pre-start'
 *   Coach step 2 (Enter prompt)        → 'typing'
 *   Coach step 3 (Prompt → JSON)       → 'parsing' + 'lanes'
 *   Coach step 4 (Submit to Darwin)    → brief tail of 'lanes' before 'streaming'
 *   Coach step 5 (Results)             → 'streaming' + 'celebrate' + 'settled'
 *
 * Step 4 is the trickiest: it's a ~700ms window where the submit button
 * should flash before the result pill appears. We piggyback on the
 * 'streaming' phase's first 700ms via a delayed transition (see the
 * useEffect below).
 */
/**
 * Coach step 4 (Submit to Darwin) lives in a sub-window at the start of
 * the 'streaming' phase. The empty-state demo's phase machine doesn't
 * have a dedicated 'submit' phase — it transitions lanes → streaming
 * directly — so we synthesise the submit highlight via a time-based
 * sub-state inside this component (see useEffect below).
 *
 * Matt 2026-05-26: "the cyan transition for 'submit to darwin' happens
 * way too fast, the steps 3, 4, 5 need to be slowed down a lot".
 * Lengthened from 700ms → 1800ms so the user has time to register the
 * submit button before the highlight moves to the result pill.
 *
 * Matt 2026-05-26 23:07: "Triple the time it takes for each step
 * through the process". Lengthened from 1800ms → 5400ms to stay
 * aligned with EmptyStateDemo's STREAMING_INITIAL_HOLD_MS (also 5400).
 */
// History: original 1800ms → PR #127 ×3 → 5400ms → 2026-05-27 ×0.7 → 3800ms.
// Kept aligned with EmptyStateDemo's STREAMING_INITIAL_HOLD_MS (also 3800).
// Exported 2026-05-28 so EmptyStateDemo can keep the sticky-note step
// indicator in sync with the overlay sub-phase (Matt mismatch fix).
export const SUBMIT_HIGHLIGHT_MS = 3800

function targetForPhase(
  phase: OnboardingHighlightPhase,
  msInStreamingPhase: number,
): HighlightTarget | null {
  switch (phase) {
    case 'pre-start':
      // Pad generously — the add-video button is 28×28 on desktop, 44×44
      // on mobile, so we add extra padding to make the glow obvious.
      return { testid: 'add-video-btn', kind: 'glow', pad: 10 }
    case 'typing':
      return { testid: 'prompt-textarea', kind: 'scribble', pad: 2 }
    case 'parsing':
    case 'lanes':
      return { testid: 'compiled-query-card', kind: 'scribble', pad: 4 }
    case 'streaming':
      // First SUBMIT_HIGHLIGHT_MS of streaming: light up the submit button
      // (coach step 4). After that: light up the narrative log (coach
      // step 5).
      if (msInStreamingPhase < SUBMIT_HIGHLIGHT_MS) {
        return { testid: 'submit-prompt-btn', kind: 'glow', pad: 10 }
      }
      // Prefer the narrative-log testid over result-pill so the glow
      // wraps just the answer (Matt 2026-05-26: "the glow on step 5 is
      // too broad, it's highlighting the whole results container"). The
      // narrative-log testid is inside the result pill; if it isn't
      // present yet (e.g. first frame of streaming before NarrativeLog
      // mounts), measure() returns null and the overlay simply doesn't
      // render until it does — graceful no-op.
      return { testid: 'narrative-log', kind: 'glow', pad: 8 }
    case 'celebrate':
    case 'settled':
      return { testid: 'narrative-log', kind: 'glow', pad: 8 }
    case 'dismissed':
    default:
      return null
  }
}

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

function measure(testid: string): Rect | null {
  if (typeof document === 'undefined') return null
  const el = document.querySelector<HTMLElement>(`[data-testid="${testid}"]`)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) return null
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

interface OnboardingHighlightsProps {
  phase: OnboardingHighlightPhase
  /** When true, overlay never mounts. Useful for the persistent-dismiss path. */
  disabled?: boolean
}

// Gating: share the coach's VITE_ONBOARDING_COACH_ENABLED flag + persistent
// dismiss key so the highlight overlay only renders when the coach itself
// would render. Keeps the surface area honest for users who opted out.
const DISMISS_KEY = 'pv2-onboarding-coach-dismissed'

function isFlagEnabled(): boolean {
  try {
    const metaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    if (metaEnv?.VITE_ONBOARDING_COACH_ENABLED === 'true') return true
  } catch { /* ignore */ }
  try {
    if (typeof process !== 'undefined' && process.env?.VITE_ONBOARDING_COACH_ENABLED === 'true') return true
  } catch { /* ignore */ }
  return false
}

function isPersistentlyDismissed(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY) === 'true'
  } catch {
    return false
  }
}

// Yellow scribble — very light alpha so it reads as a faint highlighter
// wash rather than a bold marker. The two-sweep layered SVG paths compose
// to about 2× density at peak overlap, and the mix-blend-mode:multiply
// further darkens over light backgrounds, so we run the source alpha very
// low (18%) to land at "soft post-it" on screen. Matt 2026-05-26 (twice):
// "the yellow highlights are too dark, they need to be more transparent".
const SCRIBBLE_COLOR = 'rgba(253, 224, 71, 0.18)' // tailwind yellow-300 @ 18%
// Glow uses a stronger alpha so it's visible against the off-white
// (#F5F1E8) page background. Tested in pre-start (small 28px add-video
// button) where a weaker alpha looked washed out.
const GLOW_COLOR = 'rgba(34, 211, 238, 0.65)' // tailwind cyan-400 @ 65%

export function OnboardingHighlights({ phase, disabled }: OnboardingHighlightsProps): ReactElement | null {
  // Same gating as OnboardingCoach — if the flag is off, or the user
  // persistent-dismissed the coach, the highlights also don't render.
  // Compute gating up front so hook order stays stable; the early return
  // happens AFTER the hooks have been declared.
  const enabled = isFlagEnabled() && !isPersistentlyDismissed()

  // Track time since 'streaming' phase started so we can synthesise the
  // submit-button highlight (700ms) before transitioning to the result-pill
  // highlight. Re-renders every 50ms while streaming is in its first window
  // — cheap and bounded.
  const [streamingMs, setStreamingMs] = useState<number>(0)
  const streamingStartedAt = useRef<number | null>(null)

  useEffect(() => {
    if (phase !== 'streaming') {
      streamingStartedAt.current = null
      setStreamingMs(0)
      return
    }
    if (streamingStartedAt.current === null) {
      streamingStartedAt.current = Date.now()
    }
    const start = streamingStartedAt.current
    setStreamingMs(Date.now() - start)
    if (Date.now() - start >= SUBMIT_HIGHLIGHT_MS) return
    const t = window.setTimeout(
      () => setStreamingMs(Date.now() - start),
      Math.max(50, SUBMIT_HIGHLIGHT_MS - (Date.now() - start)),
    )
    return () => window.clearTimeout(t)
  }, [phase, streamingMs])

  const target = enabled && !disabled ? targetForPhase(phase, streamingMs) : null
  const [rect, setRect] = useState<Rect | null>(null)
  const lastTestid = useRef<string | null>(null)

  // Re-measure on phase change + window resize/scroll + ANY size/content
  // change to the target element itself. The last one is critical for
  // step 5 (narrative-log): new narrative lines stream in over several
  // seconds and the log container grows on each one, so the glow needs
  // to grow with it. Matt 2026-05-26 16:51: "on step 4 [→ step 5] the
  // cyan box highlights only the first line of the narrative text.
  // Then more text lines come in. The cyan box needs to expand on each
  // new line of narrative text".
  //
  // Strategy:
  //   1. Initial measure + 3-frame settling burst (16/50/280ms) for
  //      animations like the pv2-card-expand swim-lane.
  //   2. ResizeObserver on the target — fires whenever its bounding box
  //      changes (children added, font load, etc.). This is what makes
  //      the narrative-log glow track each new line.
  //   3. MutationObserver as a belt-and-braces fallback for browsers
  //      where ResizeObserver doesn't fire reliably on subtree changes
  //      that don't change the immediate target's rect.
  //   4. window resize + scroll listeners for viewport-level changes.
  useEffect(() => {
    if (!target) {
      setRect(null)
      lastTestid.current = null
      return
    }

    // 2026-05-28 (Matt #engineering-jeff bug repro): when the target changes
    // (e.g. lanes → streaming = compiled-query-card → submit-prompt-btn),
    // the first render after the testid flip still sees the OLD `rect`
    // because setRect(measure(…)) is batched. The overlay would briefly
    // paint the NEW pad/kind at the OLD target's coordinates — e.g. a glow
    // landing where the previous scribble was. Reproducible on slow mobile
    // networks where the React commit + paint races the next animation
    // frame.
    //
    // Fix: when the testid actually changes from one phase to the next,
    // clear the rect synchronously so the overlay simply isn't rendered
    // until the new measurement comes back. One render of "nothing here"
    // is far better than one render of "the right element in the wrong
    // place".
    if (lastTestid.current !== null && lastTestid.current !== target.testid) {
      setRect(null)
    }

    const measureNow = () => setRect(measure(target.testid))

    // 1. Initial measure + 3-frame settling pass.
    measureNow()
    const t1 = window.setTimeout(measureNow, 50)
    const t2 = window.setTimeout(measureNow, 280)
    lastTestid.current = target.testid

    // 2 + 3: observe the target's size + children mutations directly.
    // We resolve the element lazily because the testid may not be in the
    // DOM at effect-mount time (e.g. result-pill before streaming).
    let resizeObserver: ResizeObserver | null = null
    let mutationObserver: MutationObserver | null = null
    let pollHandle: number | null = null

    const attachObservers = () => {
      const el = document.querySelector<HTMLElement>(`[data-testid="${target.testid}"]`)
      if (!el) return false
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => measureNow())
        resizeObserver.observe(el)
      }
      mutationObserver = new MutationObserver(() => measureNow())
      mutationObserver.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      })
      return true
    }

    if (!attachObservers()) {
      // Target not in DOM yet — retry every 100ms until it appears or the
      // effect cleans up. Bounded by the cleanup so we can't leak.
      pollHandle = window.setInterval(() => {
        if (attachObservers()) {
          if (pollHandle !== null) window.clearInterval(pollHandle)
          pollHandle = null
        }
      }, 100)
    }

    // 4. Viewport-level events.
    const onResize = () => measureNow()
    window.addEventListener('resize', onResize, { passive: true })
    window.addEventListener('scroll', onResize, { passive: true, capture: true })

    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      if (pollHandle !== null) window.clearInterval(pollHandle)
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, { capture: true } as EventListenerOptions)
    }
  }, [target?.testid])

  if (!target || !rect) return null

  // Portal into document.body so the overlay's `position: fixed` is
  // resolved against the viewport, not against any ancestor transform.
  // (One of the EmptyStateDemo ancestors uses translateZ/matrix to GPU-
  // promote layers, which creates a containing block for fixed children
  // and was causing the highlight to land in the wrong screen position.)
  if (typeof document === 'undefined') return null

  const pad = target.pad ?? 4
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
    pointerEvents: 'none',
    zIndex: 9999,
    // 220ms ease-out keeps highlight transitions snappy but not jumpy.
    transition: 'top 220ms ease-out, left 220ms ease-out, width 220ms ease-out, height 220ms ease-out',
  }

  const highlightNode =
    target.kind === 'scribble' ? (
      <div
        data-testid="onboarding-highlight-scribble"
        data-testid-target={target.testid}
        aria-hidden="true"
        style={overlayStyle}
      >
        <ScribbleSvg width={rect.width + pad * 2} height={rect.height + pad * 2} />
      </div>
    ) : (
      <div
        data-testid="onboarding-highlight-glow"
        data-testid-target={target.testid}
        aria-hidden="true"
        style={{
          ...overlayStyle,
          borderRadius: 12,
          boxShadow: `0 0 0 3px ${GLOW_COLOR}, 0 0 32px 8px ${GLOW_COLOR}`,
          animation: 'pv2-highlight-pulse 2.4s ease-in-out infinite',
        }}
      />
    )

  return createPortal(highlightNode, document.body)
}

/**
 * Hand-drawn marker scribble. Two diagonal sweeps with slight roughness so
 * it reads as "hand drawn" rather than a solid yellow rectangle. The SVG
 * scales to its container so it always covers the underlying text.
 *
 * Implementation: 2 overlapping paths at slightly different angles, both
 * yellow @ 55% alpha, mix-blend-mode multiply to behave like a real
 * highlighter when there's text underneath.
 */
function ScribbleSvg({ width, height }: { width: number; height: number }): ReactElement {
  // Two strokes diagonal-ish. We keep the path simple and let it scale.
  // viewBox is 100×40 so the strokes look consistent at any aspect ratio.
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        inset: 0,
        mixBlendMode: 'multiply',
        animation: 'pv2-highlight-fade-in 280ms ease-out both',
      }}
    >
      {/* Single soft sweep, vertically centered around y=20. We deliberately
         dropped the original second layered sweep — multiplying two SVG
         paths through mix-blend-mode:multiply made the yellow look like
         a bold marker even at 32% alpha. One sweep at 18% reads as a
         faint highlighter wash that lets the text underneath stay
         legible. */}
      <path
        d="M 1 22 C 22 18, 48 24, 72 19 S 96 18, 99 20"
        stroke={SCRIBBLE_COLOR}
        strokeWidth={32}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}
